// Environment switcher. A deployment can present dev / stage / prod at one URL:
// the chosen env lives in the `env` cookie, and the edge gateway (see
// docs/routing.md) serves the matching stage. This module renders the on-page
// control and remembers the choice.

export const ENV_COOKIE = 'env';

// Pure: keep the value only if it is one of the known envs, else the fallback.
export function resolveEnv(raw, options, fallback) {
  const value = String(raw ?? '').trim();
  return options.includes(value) ? value : fallback;
}

export function readCookie(name, cookieString) {
  const source = cookieString ?? (typeof document !== 'undefined' ? document.cookie : '') ?? '';
  const prefix = name + '=';
  const part = source.split('; ').find((entry) => entry.startsWith(prefix));
  return part ? decodeURIComponent(part.slice(prefix.length)) : '';
}

export function writeEnv(id, name = ENV_COOKIE) {
  document.cookie = `${name}=${encodeURIComponent(id)};path=/;max-age=31536000;samesite=lax`;
}

export function initEnvSwitcher(root = document, opts = {}) {
  const switchers = root.querySelectorAll('.env-switcher');
  if (!switchers.length) return;
  const host = opts.host ?? (typeof location !== 'undefined' ? location.host : '');
  const reload = opts.reload ?? (() => location.reload());

  switchers.forEach((el) => {
    const prodHost = el.dataset.envProdHost || '';
    const fallback = el.dataset.envDefault || 'prod';
    const buttons = Array.from(el.querySelectorAll('[data-env]'));
    const options = buttons.map((button) => button.dataset.env);
    const current = resolveEnv(readCookie(ENV_COOKIE), options, fallback);

    buttons.forEach((button) => {
      const isCurrent = button.dataset.env === current;
      button.classList.toggle('is-active', isCurrent);
      button.setAttribute('aria-pressed', String(isCurrent));
    });

    // Never show the control on the production domain.
    if (prodHost && host === prodHost) {
      el.hidden = true;
      return;
    }
    el.hidden = false;

    el.addEventListener('click', (event) => {
      const button = event.target.closest('[data-env]');
      if (!button || button.dataset.env === current) return;
      writeEnv(button.dataset.env);
      reload();
    });
  });
}

if (typeof document !== 'undefined') {
  const boot = () => initEnvSwitcher(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}
